import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useSearchParams } from 'react-router-dom';
import { passwordSchema } from '@pandacalcio/validation';
import { z } from 'zod';
import { api, ApiError } from '../../lib/api';
import { useAnnouncer } from '../../components/LiveAnnouncer';
import { usePageTitle } from '../../lib/usePageTitle';
import { AuthLayout } from '../../components/AuthLayout';
import { TextField } from '../../components/TextField';
import { Button } from '../../components/Button';

const formSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Le due password non coincidono',
  });

type Input = z.infer<typeof formSchema>;

export function ResetPasswordPage() {
  usePageTitle('Reimposta password');
  const [searchParams] = useSearchParams();
  const { announce } = useAnnouncer();
  const [serverError, setServerError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const token = searchParams.get('token') ?? '';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Input>({ resolver: zodResolver(formSchema) });

  const onSubmit = handleSubmit(async (input) => {
    setServerError(null);
    try {
      await api('/auth/reset-password', {
        method: 'POST',
        body: { token, password: input.password },
      });
      setDone(true);
      announce('Password aggiornata. Ora puoi accedere con la nuova password.');
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'Qualcosa è andato storto. Riprova.';
      setServerError(message);
      announce(`Errore: ${message}`, 'assertive');
    }
  });

  if (!token) {
    return (
      <AuthLayout title="Reimposta password">
        <p role="alert" className="font-medium text-danger">
          Link non valido: manca il codice di reset. Richiedi un nuovo link.
        </p>
        <p className="mt-6">
          <Link to="/password-dimenticata" className="text-panda-700 underline dark:text-panda-300">
            Richiedi un nuovo link
          </Link>
        </p>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout title="Password aggiornata">
        <p role="status" className="text-lg">
          <span aria-hidden="true">✓ </span>La tua password è stata aggiornata.
        </p>
        <p className="mt-6">
          <Link to="/accedi" className="text-panda-700 underline dark:text-panda-300">
            Vai alla pagina di accesso
          </Link>
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Reimposta password">
      {serverError && (
        <div
          role="alert"
          className="mb-4 rounded-lg border-2 border-danger bg-red-50 p-3 font-medium text-danger dark:bg-ink-800"
        >
          {serverError}
        </div>
      )}
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <TextField
          label="Nuova password"
          type="password"
          revealable
          description="Almeno 10 caratteri, con almeno una lettera e un numero."
          autoComplete="new-password"
          required
          error={errors.password?.message}
          {...register('password')}
        />
        <TextField
          label="Conferma nuova password"
          type="password"
          revealable
          autoComplete="new-password"
          required
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />
        <Button type="submit" loading={isSubmitting} loadingText="Aggiornamento in corso…">
          Aggiorna password
        </Button>
      </form>
    </AuthLayout>
  );
}
